<script lang="ts" setup>
// 导入图片
import coreModule1 from '@/assets/images/productHardware_core_module_first.png'
import coreModule2 from '@/assets/images/productHardware_core_module_second.png'
import coreModule3 from '@/assets/images/productHardware_core_module_third.png'
import coreModule4 from '@/assets/images/productHardware_core_module_fourth.png'
import coreModule5 from '@/assets/images/productHardware_core_module_five.png'
import coreModule6 from '@/assets/images/productHardware_core_module_six.png'
import one from '@/assets/images/1000bit/one.png'
import two from '@/assets/images/1000bit/two.png'
import three from '@/assets/images/1000bit/three.png'
import four from '@/assets/images/1000bit/four.png'
const { trackEvent } = useCnzz()

const { t } = useI18n()

definePageMeta({ layout: 'page' })
useHead({
  title: t('productHardware.pageTitle'),
  meta: [
    {
      name: 'description',
      content: t('seo.product.description'),
    },
    {
      name: 'keywords',
      content: t('seo.product.keywords'),
    },
    {
      property: 'og:title',
      content: t('seo.product.title'),
    },
    {
      property: 'og:description',
      content: t('seo.product.description'),
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      property: 'og:locale',
      content: 'zh_CN',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: t('seo.product.title'),
    },
    {
      name: 'twitter:description',
      content: t('seo.product.description'),
    },
    {
      name: 'twitter:image',
      content: 'https://www.qboson.com/favicon.ico',
    },
    {
      name: 'author',
      content: '玻色量子',
    },
  ],
})

// 轮播图逻辑
const currentSlide = ref(0)
const slides = computed(() => [
  {
    title: t('productHardware.coreModule.module1.subtitle'),
    description: t('productHardware.coreModule.module1.description'),
    image: coreModule1,
  },
  {
    title: t('productHardware.coreModule.module2.subtitle'),
    description: t('productHardware.coreModule.module2.description'),
    image: coreModule2,
  },
  {
    title: t('productHardware.coreModule.module3.subtitle'),
    description: t('productHardware.coreModule.module3.description'),
    image: coreModule3,
  },
  {
    title: t('productHardware.coreModule.module4.subtitle'),
    description: t('productHardware.coreModule.module4.description'),
    image: coreModule4,
  },
  {
    title: t('productHardware.coreModule.module5.subtitle'),
    description: t('productHardware.coreModule.module5.description'),
    image: coreModule5,
  },
  {
    title: t('productHardware.coreModule.module6.subtitle'),
    description: t('productHardware.coreModule.module6.description'),
    image: coreModule6,
  },
])

// 无限轮播相关
const isTransitioning = ref(true)

// 计算图片在轮播中的位置（-1=左侧, 0=中间, 1=右侧）
const getSlidePosition = (slideIndex: number) => {
  const total = slides.value.length
  const diff = (slideIndex - currentSlide.value + total) % total

  // 将差值标准化到 [0, total) 范围
  // 然后判断位置关系
  if (diff === 0) return 0 // 中间（当前卡片）
  if (diff === 1) return 1 // 右侧（下一张）
  if (diff === total - 1) return -1 // 左侧（上一张）

  return null // 不显示
}

// 判断是否是左侧小图
const isLeftSlide = (slideIndex: number) => {
  return getSlidePosition(slideIndex) === -1
}

// 判断是否是中间大图
const isCenterSlide = (slideIndex: number) => {
  return getSlidePosition(slideIndex) === 0
}

// 判断是否是右侧小图
const isRightSlide = (slideIndex: number) => {
  return getSlidePosition(slideIndex) === 1
}

// 判断卡片是否可见
const isCardVisible = (slideIndex: number) => {
  return getSlidePosition(slideIndex) !== null
}

let autoPlayTimer: NodeJS.Timeout | null = null

// 切换到指定幻灯片
const goToSlide = (index: number) => {
  if (index === currentSlide.value) return
  currentSlide.value = index
  resetAutoPlay()
}

// 下一张
const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % slides.value.length
  resetAutoPlay()
}

// 上一张
const prevSlide = () => {
  currentSlide.value =
    (currentSlide.value - 1 + slides.value.length) % slides.value.length
  resetAutoPlay()
}

// 触摸滑动相关
const touchStartX = ref(0)
const touchEndX = ref(0)
const touchStartTime = ref(0)

const handleTouchStart = (e: TouchEvent) => {
  touchStartX.value = e.touches[0].clientX
  touchStartTime.value = Date.now()
}

const handleTouchMove = (e: TouchEvent) => {
  touchEndX.value = e.touches[0].clientX
}

const handleTouchEnd = () => {
  const diff = touchStartX.value - touchEndX.value
  const duration = Date.now() - touchStartTime.value
  const threshold = 30 // 降低滑动阈值，更容易触发

  // 只要滑动距离超过阈值就触发切换
  if (Math.abs(diff) > threshold) {
    if (diff > 0) {
      // 向左滑动，显示下一张
      nextSlide()
    } else {
      // 向右滑动，显示上一张
      prevSlide()
    }
  }

  touchStartX.value = 0
  touchEndX.value = 0
  touchStartTime.value = 0
}

// 处理卡片点击
const handleCardClick = (index: number) => {
  // 重置触摸状态，防止触摸事件干扰
  touchStartX.value = 0
  touchEndX.value = 0
  touchStartTime.value = 0

  if (isLeftSlide(index)) {
    prevSlide()
  } else if (isRightSlide(index)) {
    nextSlide()
  }
}

// 自动播放
const startAutoPlay = () => {
  autoPlayTimer = setInterval(() => {
    nextSlide()
  }, 5000)
}

// 重置自动播放
const resetAutoPlay = () => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
  }
  startAutoPlay()
}

// 组件挂载时启动自动播放
onMounted(() => {
  startAutoPlay()
  trackEvent('硬件产品', '访问', '产品介绍页面')
})

// 组件卸载时清除定时器
onUnmounted(() => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
  }
})

// 四大优势交互逻辑
const activeAdvantage = ref(0) // 默认第一个展开

const advantages = computed(() => [
  {
    title: t('productHardware.advantages.advantage1.title'),
    description: t('productHardware.advantages.advantage1.description'),
    img: one,
  },
  {
    title: t('productHardware.advantages.advantage2.title'),
    description: t('productHardware.advantages.advantage2.description'),
    img: two,
  },
  {
    title: t('productHardware.advantages.advantage3.title'),
    description: t('productHardware.advantages.advantage3.description'),
    img: three,
  },
  {
    title: t('productHardware.advantages.advantage4.title'),
    description: t('productHardware.advantages.advantage4.description'),
    img: four,
  },
])

const setActiveAdvantage = (index: number) => {
  activeAdvantage.value = index
}

// 在线咨询弹窗逻辑
const showConsultDialog = ref(false)

// 打开弹窗
const openDialog = () => {
  showConsultDialog.value = true
  trackEvent('硬件产品', '点击', '整机咨询按钮')
}

const showBusinessDialogDialog = ref(false)

const openBusinessDialogDialog = (item: any) => {
  showBusinessDialogDialog.value = true
  trackEvent('硬件产品', '点击', `${item.btnText}-产品手册按钮`)
}

// Hero 按钮数据
const heroButtons = computed(() => [
  {
    key: 'consult',
    label: t('productHardware.hero.btnConsult'),
    to: '',
  },
  {
    key: 'learn',
    label: t('productHardware.hero.btnLearn'),
    to: '/product/product1000bit',
    new: true,
  },
  {
    key: 'compare',
    label: t('productHardware.hero.btnCompare'),
    to: '/product/productSpecs',
  },
])

// 当前选中的产品索引，默认选中第一个
const activeProductIndex = ref(0)
const slideDirection = ref('slide-left')

watch(activeProductIndex, (newVal, oldVal) => {
  if (newVal > oldVal) {
    slideDirection.value = 'slide-left'
  } else {
    slideDirection.value = 'slide-right'
  }
})

// 移动端当前选中的产品索引
const activeMobileProductIndex = ref(0)

// 移动端产品卡片滑动逻辑
let productTouchStartX = 0
let productTouchEndX = 0

const handleProductTouchStart = (e: TouchEvent) => {
  productTouchStartX = e.changedTouches[0].screenX
  // 纯点击通常不会触发 touchmove；若仍以初始 0 为终点会误判成大距离滑动
  productTouchEndX = productTouchStartX
}

const handleProductTouchMove = (e: TouchEvent) => {
  productTouchEndX = e.changedTouches[0].screenX
}

const handleProductTouchEnd = (e: TouchEvent) => {
  const endX = e.changedTouches[0]?.screenX ?? productTouchEndX
  const diffX = productTouchStartX - endX
  if (Math.abs(diffX) > 50) {
    if (diffX > 0) {
      // 向左滑，下一张
      if (activeMobileProductIndex.value < products.value.length - 1) {
        activeMobileProductIndex.value++
      } else {
        // 如果到了最后一张，循环回第一张
        activeMobileProductIndex.value = 0
      }
    } else if (activeMobileProductIndex.value > 0) {
      // 向右滑，上一张
      activeMobileProductIndex.value--
    } else {
      // 如果到了第一张，循环到最后一张
      activeMobileProductIndex.value = products.value.length - 1
    }
  }
}

const isProductCardVisible = (index: number) => {
  const len = products.value.length
  const current = activeMobileProductIndex.value

  if (index === current) return true
  if (index === (current - 1 + len) % len) return true
  if (index === (current + 1) % len) return true
  return false
}

const isProductLeftSlide = (index: number) => {
  const len = products.value.length
  return index === (activeMobileProductIndex.value - 1 + len) % len
}

const isProductCenterSlide = (index: number) => {
  return index === activeMobileProductIndex.value
}

const isProductRightSlide = (index: number) => {
  const len = products.value.length
  return index === (activeMobileProductIndex.value + 1) % len
}

const handleProductCardClick = (index: number) => {
  if (isProductLeftSlide(index)) {
    activeMobileProductIndex.value =
      (activeMobileProductIndex.value - 1 + products.value.length) %
      products.value.length
  } else if (isProductRightSlide(index)) {
    activeMobileProductIndex.value =
      (activeMobileProductIndex.value + 1) % products.value.length
  }
}

const goToProductSlide = (index: number) => {
  activeMobileProductIndex.value = index
}

// 产品卡片数据
const products = computed(() => [
  {
    key: '1000bit-shanhai',
    title: t('productHardware.products.1000bit-shanhai.title'),
    subtitle: t(
      'productHardware.products.1000bit-shanhai.subtitle',
      '驭量·山海1000',
    ),
    secondtitle: t(
      'productHardware.products.1000bit-shanhai.secondtitle',
      '相关光量子计算机',
    ),
    description: t(
      'productHardware.products.1000bit-shanhai.description',
      '国内首款最高支持 3000 比特规模且可长时间稳定运行的专用量子计算机，支持 3 种运算模式。搭载更高等级的智能控制系统，具有更强计算能力、更广泛应用前景和更强的扩展性。',
    ),
    btnText: t(
      'productHardware.products.1000bit-shanhai.btnManual',
      '了解更多',
    ),
    btnDownload: t('productHardware.products.1000bit.btnDownload'),
    btnColor: 'cyan',
    btnLink: '/product/product1000-shanhai',
    img: 'https://images-cloud.qboson.com/website/shanhai1000/shanhai.png',
  },
  {
    key: '1000bit',
    title: t('productHardware.products.1000bit.title'),
    subtitle: t(
      'productHardware.products.1000bit.subtitle',
      '1000量子比特专用量子计算机',
    ),
    secondtitle: t(
      'productHardware.products.1000bit.secondtitle',
      '相关光量子计算机',
    ),
    description: t('productHardware.products.1000bit.description'),
    btnText: t('productHardware.products.1000bit.btnManual', '了解更多'),
    btnDownload: t('productHardware.products.1000bit.btnDownload'),
    btnColor: 'cyan',
    btnLink: '/product/product1000bit',
    img: 'https://images-cloud.qboson.com/website/1000bit.png',
  },
  {
    key: '550bit',
    title: t('productHardware.products.550bit.title'),
    subtitle: t(
      'productHardware.products.550bit.subtitle',
      '550量子比特专用量子计算机',
    ),
    secondtitle: t(
      'productHardware.products.550bit.secondtitle',
      '相关光量子计算机',
    ),
    description: t('productHardware.products.550bit.description'),
    btnText: t('productHardware.products.550bit.btnManual', '了解更多'),
    btnDownload: t('productHardware.products.550bit.btnDownload'),
    btnColor: 'blue',
    btnLink: '/product/product550bit',
    img: 'https://images-cloud.qboson.com/website/product/550.png',
  },
  {
    key: '100bit',
    title: t('productHardware.products.100bit.title'),
    subtitle: t(
      'productHardware.products.100bit.subtitle',
      '100量子比特专用量子计算机',
    ),
    secondtitle: t(
      'productHardware.products.100bit.secondtitle',
      '相关光量子计算机',
    ),
    description: t('productHardware.products.100bit.description'),
    btnText: t('productHardware.products.100bit.btnManual', '了解更多'),
    btnDownload: t('productHardware.products.100bit.btnDownload'),
    btnColor: 'red',
    btnLink: '/product/product100bit',
    img: 'https://images-cloud.qboson.com/website/product/100.png',
  },
])

// 头图按钮-友盟上报
const trackEventHeroButtons = (item: any) => {
  trackEvent('硬件产品', '点击', `${item.label}按钮`)
}
</script>

<template>
  <div class="min-h-screen w-full">
    <!-- web端 -->
    <div class="hidden md:block min-h-screen w-full bg-[#FFF]">
      <!-- 产品介绍+产品卡片 -->
      <section class="pt-[155px] pb-[60px] max-w-[1200px] mx-auto">
        <!-- 产品介绍 -->
        <div class="relative h-[325px] mb-[101px] w-full overflow-hidden">
          <transition :name="slideDirection" mode="out-in">
            <div :key="activeProductIndex" class="flex w-full h-full">
              <div
                class="w-[492px] mr-[154px] mt-[13px] flex flex-col justify-center flex-shrink-0"
              >
                <h1 class="text-[32px] font-bold text-[#000] mb-[16px]">
                  {{ products[activeProductIndex].subtitle }}
                </h1>
                <p
                  class="text-[18px] text-[#666] mb-[31px] font-normal leading-[30px] min-h-[180px]"
                >
                  {{ products[activeProductIndex].description }}
                </p>
                <div class="flex items-center gap-[28px]">
                  <!-- 咨询按钮保持不变 -->
                  <button
                    class="px-[26px] py-[6px] text-[14px] text-[#333] font-medium cursor-pointer gradient-border hover:opacity-80 transition-opacity"
                    @click="openDialog"
                  >
                    {{ t('productHardware.hero.btnConsult') }}
                  </button>

                  <!-- 动态跳转的了解更多按钮 -->
                  <NuxtLink
                    v-if="
                      products[activeProductIndex].key !== '550bit' &&
                      products[activeProductIndex].key !== '100bit'
                    "
                    :to="products[activeProductIndex].btnLink"
                    class="relative px-[26px] py-[6px] text-[14px] text-[#333] font-medium cursor-pointer gradient-border hover:opacity-80 transition-opacity inline-block"
                  >
                    {{ products[activeProductIndex].btnText }}
                    <span
                      v-if="
                        products[activeProductIndex].key === '1000bit-shanhai'
                      "
                      class="absolute -top-[8px] -right-[20px] px-[10px] py-[4px] text-[12px] text-white rounded-[4px] flex items-center justify-center font-normal leading-none"
                      style="
                        background: linear-gradient(
                          124deg,
                          #36ba93 -2.73%,
                          #37a9c5 50.34%,
                          #073c89 103.64%
                        );
                      "
                    >
                      NEW
                    </span>
                  </NuxtLink>

                  <!-- 产品对比保持不变 -->
                  <NuxtLink
                    to="/product/productSpecs"
                    class="relative px-[26px] py-[6px] text-[14px] text-[#333] font-medium cursor-pointer gradient-border hover:opacity-80 transition-opacity inline-block"
                  >
                    {{ t('productHardware.hero.btnCompare') }}
                  </NuxtLink>
                </div>
              </div>
              <div class="flex-1 h-full flex items-center justify-center">
                <img
                  class="max-w-[485px] max-h-[303px] object-contain"
                  :src="products[activeProductIndex].img"
                  :alt="products[activeProductIndex].title"
                />
              </div>
            </div>
          </transition>
        </div>
        <!-- 产品卡片 -->
        <div
          class="flex justify-between items-center w-[1200px] h-[113px] px-[4px] mx-auto bg-white rounded-[12px] shadow-[0px_7px_30px_8px_#DAE0ED] overflow-hidden cursor-pointer relative"
        >
          <!-- Animated Background block for sliding effect -->
          <div
            class="absolute top-1/2 -translate-y-1/2 h-[104px] rounded-[12px] transition-all duration-300 ease-in-out"
            style="
              background: linear-gradient(
                83deg,
                #36ba93 0%,
                #37a9c5 50.11%,
                #073c89 100%
              );
              z-index: 0;
            "
            :style="{
              width: `calc((100% - 8px) / ${products.length})`,
              left: `calc(4px + ${activeProductIndex} * (100% - 8px) / ${products.length})`,
            }"
          ></div>

          <div
            v-for="(product, index) in products"
            :key="product.key"
            class="flex flex-col justify-center items-center h-[104px] rounded-[12px] flex-1 transition-colors duration-300 z-10"
            :class="activeProductIndex === index ? 'text-white' : 'text-black'"
            @click="activeProductIndex = index"
          >
            <p class="text-[18px] font-bold mb-[10px]">{{ product.title }}</p>
            <p
              class="text-[16px] font-normal transition-colors duration-300"
              :class="
                activeProductIndex === index ? 'text-white/80' : 'text-[#666]'
              "
            >
              {{ product.secondtitle }}
            </p>
          </div>
        </div>
      </section>

      <!-- 核心模块 -->
      <section
        class="pb-[92px] gradient-background mx-auto"
        style="min-height: auto"
      >
        <div class="max-w-[1200px] mx-auto pt-[57px]">
          <p class="text-[36px] text-[#000] font-medium text-center mb-[104px]">
            {{ t('productHardware.coreModule.title') }}
          </p>
          <!-- 轮播图 -->
          <div class="relative w-full">
            <!-- 轮播内容 -->
            <div class="flex justify-between h-full pb-[22px]">
              <!-- 左侧文字内容 -->
              <div class="w-[671px]">
                <h3 class="text-[36px] font-medium text-[#000] mb-[20px]">
                  {{ slides[currentSlide].subtitle }}
                </h3>
                <p class="text-[21px] text-[#000] leading-[34px]">
                  {{ slides[currentSlide].description }}
                </p>
              </div>

              <!-- 右侧图片 -->
              <div
                class="w-[500px] h-[350px] mt-[17px] flex items-center justify-center"
              >
                <img
                  v-if="slides[currentSlide].image"
                  :src="slides[currentSlide].image"
                  :alt="slides[currentSlide].title"
                  class="max-w-full max-h-full object-contain"
                />
                <div v-else class="text-gray-400">图片占位</div>
              </div>
            </div>

            <!-- 指示器 -->
            <div
              class="absolute bottom-0 left-1/2 transform -translate-x-1/2 flex gap-[12px]"
            >
              <button
                v-for="(_, index) in slides"
                :key="index"
                :aria-label="`Go to slide ${index + 1}`"
                class="carousel-indicator w-[45px] h-[5px] rounded-full transition-all duration-300 cursor-pointer"
                :class="
                  currentSlide === index
                    ? 'indicator-active'
                    : 'indicator-inactive'
                "
                @click="goToSlide(index)"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- 四大优势 -->
      <section
        class="h-[810px] pt-[57px] pb-[67px] bg-[#FFF] mx-auto flex flex-col"
      >
        <div class="max-w-[1200px] mx-auto">
          <p
            class="text-[36px] text-[#000] font-medium text-center mb-[90px] flex-shrink-0"
          >
            {{ t('productHardware.advantages.title') }}
          </p>
          <!-- 优势卡片容器 -->
          <div class="flex flex-1 gap-[20px]">
            <div
              v-for="(advantage, index) in advantages"
              :key="index"
              class="advantage-card relative overflow-hidden rounded-[12px] cursor-pointer transition-all duration-500 ease-in-out"
              :class="{
                'advantage-expanded': activeAdvantage === index,
                'advantage-collapsed': activeAdvantage !== index,
              }"
              @mouseenter="setActiveAdvantage(index)"
            >
              <img
                class="max-w-[366px] max-h-[260px] mx-auto mt-[20px]"
                :src="advantage.img"
                alt=""
              />
              <!-- 优势卡片内容 -->
              <div class="flex flex-col pb-[34px] px-[20px] mt-[30px]">
                <!-- 优势标题 -->
                <h3
                  class="text-[20px] font-medium text-[#000] mb-[14px] transition-all duration-500"
                  :class="{
                    'line-clamp-none': activeAdvantage === index,
                    'line-clamp-1': activeAdvantage !== index,
                  }"
                >
                  {{ advantage.title }}
                </h3>

                <!-- 优势描述 -->
                <p
                  class="text-[18px] text-[#7F8391] font-normal transition-all duration-500"
                  :class="{
                    'line-clamp-none': activeAdvantage === index,
                    'line-clamp-3': activeAdvantage !== index,
                  }"
                >
                  {{ advantage.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 业务咨询及商务合作弹窗 -->
    <ConsultationDialog v-model:show="showConsultDialog" />
    <!-- 业务咨询弹窗 -->
    <BusinessDialog v-model:show="showBusinessDialogDialog" />

    <!-- mobile端 -->
    <div class="md:hidden w-full">
      <!-- 产品介绍内容块 (全屏滑动轮播) -->
      <section class="w-full pt-[80px] pb-[40px] bg-[#F9FBFF]">
        <div class="relative w-full overflow-hidden">
          <!-- 滑动容器 -->
          <div
            class="flex touch-pan-y transition-transform duration-500 ease-out"
            :style="{
              // 百分比 translate 相对于「变换元素自身」宽度；必须与轨道宽度 n×父宽 对齐，Safari/WebKit 对 flex 隐式宽度更敏感
              width: `${products.length * 100}%`,
              transform: `translateX(-${(activeMobileProductIndex / products.length) * 100}%)`,
            }"
            @touchstart.passive="handleProductTouchStart"
            @touchmove.passive="handleProductTouchMove"
            @touchend="handleProductTouchEnd"
          >
            <!-- 每一个全屏的轮播 Item -->
            <div
              v-for="(product, index) in products"
              :key="index"
              class="flex flex-shrink-0 flex-col items-center px-[16px]"
              :style="{ width: `${100 / products.length}%` }"
            >
              <!-- 顶部产品图片 -->
              <div
                class="w-[95%] flex items-center justify-center mb-[47px] mt-[20px] h-[220px]"
              >
                <img
                  :src="product.img"
                  :alt="product.title"
                  class="w-full h-full object-contain"
                />
              </div>

              <!-- 标题和描述 -->
              <div class="w-full">
                <h1
                  class="text-[24px] font-bold text-[#000] mb-[10px] text-left"
                >
                  {{ product.subtitle }}
                </h1>
                <p
                  class="h-[156px] text-[14px] text-[#666] mb-[20px] font-normal leading-[24px] text-left"
                >
                  {{ product.description }}
                </p>
              </div>

              <!-- 操作按钮：阻止触摸冒泡到轮播容器，避免点按被当成横向滑动 -->
              <div
                class="flex items-center justify-start gap-[10px] w-full mb-[30px]"
                @touchstart.stop
                @touchmove.stop
                @touchend.stop
              >
                <NuxtLink
                  to="/product/productConsultation"
                  class="mobile-product-action-btn text-[14px] text-[#000] font-normal cursor-pointer border border-[#146AA8] bg-white rounded-[4px] hover:opacity-80 transition-opacity"
                >
                  整机咨询
                </NuxtLink>
                <NuxtLink
                  v-if="product.key !== '550bit' && product.key !== '100bit'"
                  :to="product.btnLink"
                  class="mobile-product-action-btn relative text-[14px] text-[#000] font-normal cursor-pointer border border-[#146AA8] bg-white rounded-[4px] hover:opacity-80 transition-opacity"
                >
                  {{ product.btnText }}
                  <span
                    v-if="product.key === '1000bit-shanhai'"
                    class="absolute -top-[10px] -right-[15px] px-[10px] py-[4px] text-[12px] text-white rounded-[4px] flex items-center justify-center font-normal leading-none scale-75"
                    style="
                      background: linear-gradient(
                        124deg,
                        #36ba93 -2.73%,
                        #37a9c5 50.34%,
                        #073c89 103.64%
                      );
                    "
                  >
                    NEW
                  </span>
                </NuxtLink>
                <NuxtLink
                  to="/product/productSpecs"
                  class="mobile-product-action-btn text-[14px] text-[#000] font-normal cursor-pointer border border-[#146AA8] bg-white rounded-[4px] hover:opacity-80 transition-opacity"
                >
                  产品对比
                </NuxtLink>
              </div>
            </div>
          </div>

          <!-- 指示器 -->
          <div class="flex justify-center gap-[8px] mt-[10px]">
            <button
              v-for="(_, index) in products"
              :key="index"
              class="w-[32px] h-[3px] rounded-full transition-all duration-300 cursor-pointer border-none p-0"
              :class="
                activeMobileProductIndex === index
                  ? 'bg-[#37BEEF]'
                  : 'bg-[#E5E5E5]'
              "
              :style="
                activeMobileProductIndex === index
                  ? 'background: linear-gradient(90deg, #47B897 0%, #37BEEF 100%)'
                  : ''
              "
              @click="goToProductSlide(index)"
            />
          </div>
        </div>
      </section>

      <!-- 核心模块 -->
      <section
        class="w-full py-[40px] mobile-gradient-background mobile-carousel-bg"
      >
        <!-- 标题、轮播图和文字内容的父级容器 -->
        <div class="relative pb-[40px]">
          <h2
            class="text-[16px] text-[#000] font-medium text-center mb-[25px] px-[16px]"
          >
            {{ t('productHardware.coreModule.title') }}
          </h2>

          <!-- 移动端轮播图 -->
          <div class="relative w-full">
            <!-- 横向滚动图片容器 - 卡片模式 -->
            <div
              class="relative mb-[18px] touch-pan-y overflow-hidden h-[183px]"
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
            >
              <!-- 轮播轨道 -->
              <div
                class="flex items-center justify-between w-full relative h-full"
              >
                <!-- 渲染所有图片，每张图片都根据位置动态调整样式 -->
                <div
                  v-for="(slide, index) in slides"
                  v-show="isCardVisible(index)"
                  :key="index"
                  class="carousel-card absolute top-1/2 -translate-y-1/2 flex items-center justify-center overflow-hidden rounded-[8px]"
                  :class="{
                    'carousel-card-left': isLeftSlide(index),
                    'carousel-card-center': isCenterSlide(index),
                    'carousel-card-right': isRightSlide(index),
                  }"
                  @click.stop="handleCardClick(index)"
                >
                  <!-- 卡片内容 -->
                  <div class="carousel-card-inner">
                    <img
                      :src="slide.image"
                      :alt="slide.title"
                      class="carousel-card-image"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- 文字内容 -->
            <div class="px-[16px]">
              <div>
                <h3
                  class="text-[16px] font-medium text-[#000] mb-[12px] text-center"
                >
                  {{ slides[currentSlide].title }}
                </h3>
                <p
                  class="h-[140px] text-[12px] text-[#666] leading-[20px] text-left"
                >
                  {{ slides[currentSlide].description }}
                </p>
              </div>
            </div>
          </div>

          <!-- 指示器  -->
          <div
            class="absolute bottom-0 left-0 right-0 flex justify-center gap-[8px] px-[16px]"
          >
            <button
              v-for="(_, index) in slides"
              :key="index"
              :aria-label="`Go to slide ${index + 1}`"
              class="mobile-carousel-indicator w-[32px] h-[3px] rounded-full transition-all duration-300 cursor-pointer"
              :class="
                currentSlide === index
                  ? 'mobile-indicator-active'
                  : 'mobile-indicator-inactive'
              "
              @click="goToSlide(index)"
            />
          </div>
        </div>
      </section>

      <!-- 四大优势 -->
      <section class="w-full py-[40px] bg-[#fff]">
        <h2
          class="text-[16px] text-[#000] font-medium text-center mb-[14px] px-[16px]"
        >
          {{ t('productHardware.advantages.title') }}
        </h2>
        <!-- <p class="text-[12px] text-[#666] text-center mb-[22px] px-[16px]">
          {{ t('productHardware.advantages.subtitle') }}
        </p> -->

        <!-- 横向滚动容器 -->
        <div class="pl-[16px]">
          <div class="overflow-x-auto scrollbar-hide">
            <div class="flex gap-[10px]">
              <div
                v-for="(advantage, index) in advantages"
                :key="index"
                class="border border-[#E9EBF1] mobile-advantage-card flex-shrink-0 w-[271px] h-[272px] rounded-[4px] overflow-hidden relative last:mr-[16px]"
              >
                <!-- 渐变背景 -->
                <div class="flex flex-col items-center mx-auto">
                  <img
                    class="mt-[13px] w-[170px] h-[139px]"
                    :src="advantage.img"
                    alt=""
                  />
                  <div class="mt-[10px] px-[16px]">
                    <h3 class="text-[14px] text-black font-medium mb-[8px]">
                      {{ advantage.title }}
                    </h3>
                    <p class="text-[12px] text-[#666666] font-normal">
                      {{ advantage.description }}
                    </p>
                  </div>
                </div>

                <!-- 内容 -->
                <!-- <div
                  class="relative h-full flex flex-col justify-end px-[16px] text-white"
                >
                  <h3 class="text-[14px] font-medium mb-[14px]">
                    {{ advantage.title }}
                  </h3>
                  <p class="text-[12px] text-white font-normal">
                    {{ advantage.description }}
                  </p>
                </div> -->
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
/* 左右滑动过渡动画 */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(30px);
}
.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* 渐变边框圆角 */
.gradient-border {
  position: relative;
  background: #f9fbff;
  z-index: 1;
}

.gradient-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(55, 187, 148, 1),
    rgba(1, 46, 128, 1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: -1;
}

/* 阴影 */
.gradient-shadow {
  box-shadow: 0px 7px 30px 8px #dae0ed;
}

/* 核心模块背景 */
.gradient-background {
  background-image: url('../../../assets/images/productHardware_core_bg.png');
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-position: center;
  background-attachment: scroll;
  min-height: 700px;
}

/* 轮播指示器 */
.carousel-indicator {
  box-shadow: 0px 4px 4px 0px #dae0ed;
}

.indicator-active {
  background: linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%);
}

.indicator-inactive {
  background: #ffffff;
}

/* 四大优势卡片样式 */
.advantage-expanded {
  width: 420px;
  height: 480px;
  flex-shrink: 0;
  background: #ffffff;
  box-shadow: 0px 7px 30px 8px #dae0ed;
}

.advantage-collapsed {
  width: 240px;
  height: 480px;
  flex-shrink: 0;
  background: #e9ebf1;
}

.advantage-card:hover {
  box-shadow: 0px 7px 30px 8px #dae0ed;
}

/* 文本截断样式 */
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-clamp-none {
  display: block;
  -webkit-line-clamp: unset;
  line-clamp: unset;
  -webkit-box-orient: unset;
  overflow: visible;
}

/* mobile */
.mobile-product-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: 78px;
  padding: 8px 10px;
  box-sizing: border-box;
}

.mobile-gradient-border {
  position: relative;
  background: #f9fbff;
  z-index: 1;
}
.mobile-gradient-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 1px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(55, 187, 148, 1),
    rgba(1, 46, 128, 1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: -1;
}

/* 移动端产品卡片样式 */
.product-card-shadow {
  box-shadow: 4px 4px 12px 0px rgba(87, 126, 211, 0.08);
}

.product-card-cyan {
  background: linear-gradient(0deg, #ffffff 0%, #f5fffc 100%);
}

.product-card-blue {
  background: linear-gradient(3deg, #ffffff 0%, #f4f9ff 100%);
}

.product-card-red {
  background: linear-gradient(358deg, #ffffff 0%, #fffbfb 100%);
}

/* 移动端核心模块背景 */
.mobile-gradient-background {
  background: #f9fbff;
}

/* 移动端轮播卡片阴影 */
.mobile-carousel-shadow {
  box-shadow: 0px 4px 12px 0px rgba(87, 126, 211, 0.08);
}

/* 移动端左右两侧轮播图样式 */
.mobile-side-carousel {
  background: #f9fbff;
  box-shadow: 4px 4px 12px 0px rgba(87, 126, 211, 0.08);
}

/* 移动端轮播指示器 */
.mobile-carousel-indicator {
  box-shadow: 0px 2px 4px 0px rgba(218, 224, 237, 0.5);
}

.mobile-indicator-active {
  background: linear-gradient(90deg, #36ba93 0%, #47c3e1 50%, #073c89 100%);
}

.mobile-indicator-inactive {
  background: #e5e7eb;
}

/* 移动端轮播卡片样式 - Element Plus 走马灯卡片模式 */
/* 中间卡片 290×183，左右卡片 252×161，间距10px */
/* 卡片通用样式 - 所有卡片都从屏幕中心开始定位 */
.carousel-card {
  left: 50%;
  transition:
    transform 0.4s cubic-bezier(0.23, 1, 0.32, 1),
    opacity 0.4s ease,
    z-index 0s 0.2s;
  will-change: transform, opacity;
}

.carousel-card-inner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 20px;
}

.carousel-card-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: filter 0.3s ease;
}

/* 左侧卡片 - 从中心向左偏移 */
.carousel-card-left {
  width: 252px;
  height: 161px;
  z-index: 1;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(6px);
  box-shadow: 2px 4px 16px 0px rgba(87, 126, 211, 0.12);
  opacity: 1;
  /* 向左偏移：-50%(自身居中) - 145px(中间卡片一半) - 10px(间距) - 126px(自身一半) */
  transform: translate(calc(-50% - 145px - 10px - 126px), -50%) scale(0.88);
  cursor: pointer;
}

.carousel-card-left:active {
  transform: translate(calc(-50% - 145px - 10px - 126px), -50%) scale(0.85);
}

.carousel-card-left .carousel-card-image {
  filter: brightness(0.9) saturate(0.85);
}

/* 中间大卡片 - 居中显示 */
.carousel-card-center {
  width: 290px;
  height: 183px;
  z-index: 10;
  background: white;
  box-shadow: 0px 8px 24px 0px rgba(87, 126, 211, 0.25);
  opacity: 1;
  /* 完全居中 */
  transform: translate(-50%, -50%) scale(1);
  cursor: default;
}

.carousel-card-center .carousel-card-image {
  filter: none;
}

/* 右侧卡片 - 从中心向右偏移 */
.carousel-card-right {
  width: 252px;
  height: 161px;
  z-index: 1;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(6px);
  box-shadow: 2px 4px 16px 0px rgba(87, 126, 211, 0.12);
  opacity: 1;
  /* 向右偏移：-50%(自身居中) + 145px(中间卡片一半) + 10px(间距) + 126px(自身一半) */
  transform: translate(calc(-50% + 145px + 10px + 126px), -50%) scale(0.88);
  cursor: pointer;
}

.carousel-card-right:active {
  transform: translate(calc(-50% + 145px + 10px + 126px), -50%) scale(0.85);
}

.carousel-card-right .carousel-card-image {
  filter: brightness(0.9) saturate(0.85);
}

/* 移动端四大优势卡片 */
.mobile-advantage-card {
  box-shadow: 0px 4px 12px 0px rgba(87, 126, 211, 0.15);
}

.mobile-advantage-gradient {
  height: 147px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
}

/* 隐藏滚动条 */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
